import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useLang } from '../../contexts/LangContext';
import { useAuth } from '../../contexts/AuthContext';
import { buildingUtilityApi } from '../../api/buildingUtilityApi';
import { buildingUtilitiesApi, buildingUtilityBookingsApi } from '../../api/buildingUtilitiesApi';
import { buildingsApi, Building } from '../../api/buildingsApi';
import { apartmentsApi, Apartment } from '../../api/apartmentsApi';
import { BuildingUtilityBooking, BuildingUtility, BuildingUtilityGroup, BookingStatus } from '../../types';
import { RegistrationActivityLogsPanel, RegistrationActivityLog } from '../../components/building-services/RegistrationActivityLogsPanel';
import { getUtilityBookingStatusLabel } from '../../utils/bookingStatusLabels';
import {
  buildBookingDayChips,
  dateFromDaysOffset,
  daysOffsetFromToday,
  formatIsoDateToDdMmYyyy,
  isDateWithinAdvanceBookingWindow,
  normalizeAdvanceBookingDays,
  resolveStaffAdvanceBookingDays,
  parseDdMmYyyyToIsoDate,
  addDaysToIsoDate,
  enumerateIsoDateRange,
  countInclusiveDays,
} from '../../utils/advance-booking-days.util';
import { resolveUtilityBookingServiceFee } from '../../utils/utilityBookingCost.util';
import { getApiErrorMessage } from '../../utils/apiErrors';

type SlotAvailabilityLike = {
  isUnlimited?: boolean;
  originalAvailableSlots?: number;
  availableSlots?: number;
};

/** Bookable capacity for this registration (edit mode includes slots already held by this booking). */
function getBookableSlotsRemaining(availability: SlotAvailabilityLike | null | undefined): number {
  if (!availability || availability.isUnlimited) return 999;
  return availability.originalAvailableSlots ?? availability.availableSlots ?? 0;
}

// Add interface for apartment member
interface ApartmentMember {
  id: string;
  fullName: string;
  phone?: string;
  email?: string;
  role: string;
  isPrimary: boolean;
}

export const AddEditBooking: React.FC = () => {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const buildingIdFromQuery = searchParams.get('buildingId');
  
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [apartments, setApartments] = useState<Apartment[]>([]);
  const [apartmentMembers, setApartmentMembers] = useState<ApartmentMember[]>([]);
  const [utilityGroups, setUtilityGroups] = useState<BuildingUtilityGroup[]>([]);
  const [utilities, setUtilities] = useState<BuildingUtility[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activityLogs, setActivityLogs] = useState<RegistrationActivityLog[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [formData, setFormData] = useState({
    apartmentId: '',
    apartmentMemberId: '',
    utilityId: '',
    groupId: '',
    buildingId: buildingIdFromQuery || '',
    bookingDate: '',
    startTime: '',
    endTime: '',
    status: BookingStatus.PENDING_CONFIRMATION,
    slots: '1', // Số lượng slots/quantity - defaults to 1
    totalCost: '',
    notes: ''
  });

  const [selectedUtility, setSelectedUtility] = useState<BuildingUtility | null>(null);
  const [calculatedCost, setCalculatedCost] = useState(0);
  const [depositAmount, setDepositAmount] = useState(0);
  
  // Availability checking state
  const [availabilityInfo, setAvailabilityInfo] = useState<any>(null);
  const [selectedSlotAvailability, setSelectedSlotAvailability] = useState<any>(null);
  const [timeSlots, setTimeSlots] = useState<any[]>([]);
  const [loadingAvailability, setLoadingAvailability] = useState(false);

  // New movie-ticket style booking state
  const [availableDays, setAvailableDays] = useState<Array<{
    date: string;
    dayName: string;
    dayNumber: number;
    month: string;
    isToday: boolean;
    isPast: boolean;
  }>>([]);
  const [selectedDate, setSelectedDate] = useState<string>('');
  /** Consecutive days for NOTIFICATION_ONLY (e.g. wedding 24–25/09). */
  const [bookingDayCount, setBookingDayCount] = useState(1);
  const [multiDaySlotBlocked, setMultiDaySlotBlocked] = useState<string | null>(null);
  const [availableTimeSlots, setAvailableTimeSlots] = useState<Array<{
    startTime: string;
    endTime: string;
    price: number;
    availableSlots: number;
    isAvailable: boolean;
    slotType: 'MORNING' | 'AFTERNOON' | 'EVENING' | 'NIGHT';
  }>>([]);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<string>('');
  const [bookingDateMin, setBookingDateMin] = useState('');
  const [bookingDateMax, setBookingDateMax] = useState('');
  const [manualDayOffset, setManualDayOffset] = useState('');
  const [manualDateText, setManualDateText] = useState('');
  const dayScrollerRef = useRef<HTMLDivElement>(null);
  const dayDragRef = useRef({
    active: false,
    moved: false,
    suppressClick: false,
    startX: 0,
    scrollLeft: 0,
  });

  // Time slider state for TIME_BASED utilities
  const [timeSliderData, setTimeSliderData] = useState<Array<{
    hour: string;
    isAvailable: boolean;
    isBooked: boolean;
    isSelected: boolean;
    price: number;
  }>>([]);
  const [selectedTimeRange, setSelectedTimeRange] = useState<{
    startHour: string;
    endHour: string;
    duration: number;
  } | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState<string>('');

  const isEdit = !!id;
  const isNotificationOnlyUtility =
    selectedUtility?.group?.utilityType === 'NOTIFICATION_ONLY';
  const isFixedSlotsUtility =
    selectedUtility?.group?.utilityType === 'FIXED_SLOTS';
  const supportsConsecutiveDayBooking =
    isNotificationOnlyUtility || isFixedSlotsUtility;
  const slotCapacityUnit = isNotificationOnlyUtility ? 'lượt đăng ký' : 'slot';

  const bookingEndDateIso =
    selectedDate && bookingDayCount > 1
      ? addDaysToIsoDate(selectedDate, bookingDayCount - 1)
      : selectedDate;

  const maxConsecutiveDaysFromStart = useMemo(() => {
    if (!selectedDate || !bookingDateMax) return 7;
    const dates = enumerateIsoDateRange(selectedDate, bookingDateMax);
    return Math.min(14, Math.max(1, dates.length));
  }, [selectedDate, bookingDateMax]);

  const effectiveAdvanceBookingDays = useMemo(() => {
    if (!selectedUtility) return null;
    return resolveStaffAdvanceBookingDays(selectedUtility);
  }, [selectedUtility]);

  useEffect(() => {
    if (bookingDayCount > maxConsecutiveDaysFromStart) {
      setBookingDayCount(maxConsecutiveDaysFromStart);
    }
  }, [bookingDayCount, maxConsecutiveDaysFromStart]);

  const isReadOnly =
    isEdit &&
    (formData.status === BookingStatus.COMPLETED ||
      formData.status === BookingStatus.CANCELLED);

  useEffect(() => {
    loadBuildings();
    if (isEdit) {
      loadBooking();
    }
  }, [id]);

  useEffect(() => {
    if (formData.buildingId) {
      loadApartments();
      loadUtilityGroups();
    }
  }, [formData.buildingId]);

  useEffect(() => {
    if (formData.apartmentId) {
      loadApartmentMembers();
    } else {
      setApartmentMembers([]);
      setFormData(prev => ({ ...prev, apartmentMemberId: '' }));
    }
  }, [formData.apartmentId]);

  useEffect(() => {
    if (formData.buildingId && formData.groupId) {
      loadUtilities();
    } else {
      setUtilities([]);
    }
  }, [formData.buildingId, formData.groupId]);

  // Generate available days when utility is selected
  useEffect(() => {
    if (selectedUtility) {
      generateAvailableDays();
    }
  }, [selectedUtility, effectiveAdvanceBookingDays]);

  // Check availability when date is selected
  useEffect(() => {
    if (selectedDate && selectedUtility) {
      checkAvailabilityForDate(selectedDate);
    }
  }, [selectedDate, selectedUtility]);

  useEffect(() => {
    if (
      !supportsConsecutiveDayBooking ||
      !selectedDate ||
      !selectedUtility ||
      !formData.startTime ||
      !formData.endTime ||
      bookingDayCount < 1
    ) {
      setMultiDaySlotBlocked(null);
      return;
    }

    const endIso = bookingEndDateIso || selectedDate;
    let cancelled = false;

    const run = async () => {
      const dates = enumerateIsoDateRange(selectedDate, endIso);
      for (const d of dates) {
        try {
          const availability = await buildingUtilitiesApi.checkFixedSlotsAvailabilityPerServiceHour(
            selectedUtility.id,
            d,
            isEdit ? id : undefined,
          );
          const hour = (availability.serviceHours as any[])?.find(
            (h) => h.startTime === formData.startTime && h.endTime === formData.endTime,
          );
          if (!hour) {
            if (!cancelled) {
              setMultiDaySlotBlocked(
                `Khung giờ không khả dụng ngày ${formatIsoDateToDdMmYyyy(d)}`,
              );
            }
            return;
          }
          const requestedSlots = parseInt(formData.slots, 10) || 1;
          if (
            !hour.isUnlimited &&
            (hour.availableSlots ?? 0) < requestedSlots
          ) {
            if (!cancelled) {
              setMultiDaySlotBlocked(
                `Không đủ chỗ ngày ${formatIsoDateToDdMmYyyy(d)} (cần ${requestedSlots}, còn ${hour.availableSlots ?? 0})`,
              );
            }
            return;
          }
        } catch {
          if (!cancelled) setMultiDaySlotBlocked(null);
          return;
        }
      }
      if (!cancelled) setMultiDaySlotBlocked(null);
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [
    supportsConsecutiveDayBooking,
    selectedDate,
    bookingEndDateIso,
    bookingDayCount,
    formData.startTime,
    formData.endTime,
    formData.slots,
    selectedUtility?.id,
    isEdit,
    id,
  ]);

  // Calculate pricing when utility or slots change
  useEffect(() => {
    if (formData.utilityId && formData.slots) {
      calculatePricing();
    }
  }, [formData.utilityId, formData.slots]);

  // Calculate pricing when time range changes for TIME_BASED utilities
  useEffect(() => {
    if (selectedUtility?.group?.utilityType === 'TIME_BASED' && selectedTimeRange) {
      calculatePricing();
    }
  }, [selectedTimeRange, timeSliderData]);

  // Update selected slot availability when quantity changes (NO_BOOKING only — do not simulate for staff slot pickers)
  useEffect(() => {
    const utilityType = selectedUtility?.group?.utilityType;
    if (
      utilityType === 'NOTIFICATION_ONLY' ||
      utilityType === 'FIXED_SLOTS'
    ) {
      return;
    }

    if (selectedSlotAvailability && formData.slots && selectedTimeSlot) {
      const requestedSlots = parseInt(formData.slots) || 1;
      
      if (isEdit) {
        // In edit mode, calculate how many slots would be available after the new quantity
        const excludedBookedSlots = selectedSlotAvailability.excludedBookedSlots || 0;
        const newBookedSlots = excludedBookedSlots + requestedSlots;
        const totalCapacity = selectedSlotAvailability.totalCapacity || 0;
        const newAvailableSlots = Math.max(0, totalCapacity - newBookedSlots);

        setSelectedSlotAvailability((prev: any) => ({
          ...prev,
          bookedSlots: newBookedSlots,
          availableSlots: newAvailableSlots // Update based on new quantity
        }));
      } else {
        // For new bookings, show simulation
        const originalAvailable = selectedSlotAvailability.originalAvailableSlots || selectedSlotAvailability.availableSlots;
        const originalBooked = (selectedSlotAvailability.totalCapacity || 0) - originalAvailable;
        const newAvailableSlots = Math.max(0, originalAvailable - requestedSlots);
        const newBookedSlots = originalBooked + requestedSlots;
        
        setSelectedSlotAvailability((prev: any) => ({
          ...prev,
          bookedSlots: newBookedSlots,
          availableSlots: newAvailableSlots
        }));
      }
    }
  }, [
    formData.slots,
    selectedTimeSlot,
    isEdit,
    availabilityInfo,
    formData.startTime,
    formData.endTime,
    selectedUtility?.group?.utilityType,
    selectedSlotAvailability,
  ]);

  // Set selected utility when utility ID changes
  useEffect(() => {
    if (formData.utilityId && utilities.length > 0) {
      const utility = utilities.find(u => u.id === formData.utilityId);
      if (utility) {
        setSelectedUtility(utility);
      }
    }
  }, [formData.utilityId, utilities]);

  const generateAvailableDays = () => {
    if (!selectedUtility || !effectiveAdvanceBookingDays) return;

    const { chips, minDate, maxDate } = buildBookingDayChips(
      effectiveAdvanceBookingDays,
      undefined,
      { allowNegativeMin: true },
    );
    setAvailableDays(chips);
    setBookingDateMin(minDate);
    setBookingDateMax(maxDate);

    // Auto-select first available day if not editing
    if (!isEdit && chips.length > 0) {
      setSelectedDate(chips[0].date);
      setFormData((prev) => ({ ...prev, bookingDate: chips[0].date }));
    }
  };

  const checkAvailabilityForDate = async (date: string) => {
    if (!selectedUtility || !date) return;
    
    try {
      setLoadingAvailability(true);
      const utilityType = selectedUtility.group?.utilityType;

      if (utilityType === 'FIXED_SLOTS') {
        // Check slots availability (exclude current booking if editing)
        const availability = await buildingUtilitiesApi.checkFixedSlotsAvailabilityPerServiceHour(
          selectedUtility.id, 
          date,
          isEdit ? id : undefined
        );
        setAvailabilityInfo(availability);
        
        // Generate time slots for FIXED_SLOTS
        generateFixedTimeSlots(date, availability);
        
      } else if (utilityType === 'TIME_BASED') {
        // Check time availability for TIME_BASED utilities
        const availability = await buildingUtilitiesApi.checkTimeBasedAvailability(
          selectedUtility.id,
          date
        );
        
        // Set availability info for TIME_BASED
        setAvailabilityInfo(availability);
        setTimeSlots(availability.serviceHours || []);
        
        // Extract booked slots from the availability response
        const bookedSlots: any[] = [];
        availability.serviceHours?.forEach((serviceHour: any) => {
          serviceHour.timeSlots?.forEach((timeSlot: any) => {
            if (!timeSlot.available && timeSlot.bookingId) {
              // Create a booked slot entry with approximate start/end times
              const hour = parseInt(timeSlot.hour.split(':')[0]);
              const slotDate = new Date(date);
              
              const startTime = new Date(slotDate);
              startTime.setHours(hour, 0, 0, 0);
              
              const endTime = new Date(slotDate);
              endTime.setHours(hour + 1, 0, 0, 0);
              
              bookedSlots.push({
                bookingId: timeSlot.bookingId,
                startTime: startTime.toISOString(),
                endTime: endTime.toISOString(),
                hour: timeSlot.hour
              });
            }
          });
        });
        
        // Generate time slots for TIME_BASED with pricing from utility's service hours
        generateTimeBasedSlots(date, availability.serviceHours || [], bookedSlots);
      } else if (utilityType === 'NO_BOOKING') {
        // Check availability for NO_BOOKING utilities per service hour (like library with multiple time periods)
        const availability = await buildingUtilitiesApi.checkNoBookingAvailabilityPerServiceHour(
          selectedUtility.id, 
          date,
          isEdit ? id : undefined
        );
        setAvailabilityInfo(availability);
        
        // Generate time slots for NO_BOOKING utilities similar to FIXED_SLOTS
        generateNoBookingTimeSlots(date, availability);
      } else if (utilityType === 'NOTIFICATION_ONLY') {
        const availability = await buildingUtilitiesApi.checkFixedSlotsAvailabilityPerServiceHour(
          selectedUtility.id,
          date,
          isEdit ? id : undefined,
        );
        setAvailabilityInfo(availability);
        generateFixedTimeSlots(date, availability);
      }
    } catch (err) {
      console.error('Error checking availability:', err);
      setAvailabilityInfo(null);
      setTimeSlots([]);
      setAvailableTimeSlots([]);
    } finally {
      setLoadingAvailability(false);
    }
  };

  const generateFixedTimeSlots = (date: string, availability: any) => {
    if (!availability?.serviceHours) return;

    const availabilityServiceHours = availability.serviceHours as any[];
    const slots: any[] = [];

        availabilityServiceHours.forEach(hour => {
      // Calculate price from this service hour's pricing tiers
      const price = calculateSlotPrice(hour.pricingTiers || [], 1);
      
      // For display in time slot card and availability section
      const totalCapacity = hour.totalCapacity || hour.maxCapacity || 0;
      const bookedSlots = hour.bookedSlots || 0;
      const availableSlots = hour.isUnlimited ? 999 : (hour.availableSlots || 0);

      // In edit mode, if this is the current booking's time slot, ensure correct display
      let displayAvailableSlots = availableSlots;
      if (isEdit && formData.startTime === hour.startTime && formData.endTime === hour.endTime) {
        // For the current booking's time slot in edit mode, make sure we show the correct availability
        // If the API didn't exclude the current booking properly, do it manually
        const currentBookingSlots = parseInt(formData.slots) || 1;
        if (availableSlots === totalCapacity) {
          // API didn't exclude current booking, so we do it manually
          displayAvailableSlots = Math.max(0, totalCapacity - bookedSlots - currentBookingSlots);
        } else {
          // API already excluded current booking
          displayAvailableSlots = availableSlots;
        }

      }

      slots.push({
        startTime: hour.startTime,
        endTime: hour.endTime,
        price, // This comes from the service hour's pricing tiers
        availableSlots: displayAvailableSlots, // This should be 99 in edit mode for current slot
        isAvailable: hour.isUnlimited || displayAvailableSlots > 0,
        maxCapacity: totalCapacity,
        bookedSlots: bookedSlots,
        slotType: 'SERVICE_HOUR' // Single type for all service hours
      });
    });

    setAvailableTimeSlots(slots);

    // In edit mode, automatically select the time slot after setting slots
    if (isEdit && formData.startTime && formData.endTime && !selectedSlotAvailability) {
      const slot = slots.find(s => 
        s.startTime === formData.startTime && s.endTime === formData.endTime
      );
      if (slot) {
        // Use setTimeout to ensure state is updated
        setTimeout(() => {
          handleTimeSlotSelect(slot);
        }, 50);
      }
    }
  };

  const generateTimeBasedSlots = (date: string, serviceHours: any[], bookedSlots: any[] = []) => {
    const selectedDate = new Date(date);
    const dayOfWeek = selectedDate.getDay(); // 0=Sunday, 1=Monday, ..., 6=Saturday

    const timeSlots: Array<{
      hour: string;
      isAvailable: boolean;
      isBooked: boolean;
      isSelected: boolean;
      price: number;
    }> = [];

    // Convert JavaScript day of week to database format
    // JavaScript: 0=Sunday, 1=Monday, 2=Tuesday, 3=Wednesday, 4=Thursday, 5=Friday, 6=Saturday
    // Database: 1=Monday, 2=Tuesday, 3=Wednesday, 4=Thursday, 5=Friday, 6=Saturday, 7=Sunday
    let dbDayOfWeek: number;
    if (dayOfWeek === 0) {
      dbDayOfWeek = 7; // Sunday: 0 -> 7
    } else {
      dbDayOfWeek = dayOfWeek; // Monday-Saturday: 1-6 -> 1-6
    }

    // Get pricing information from selectedUtility.serviceHours (database structure)
    const utilityServiceHours = selectedUtility?.serviceHours || [];
    
    // Find service hours that match the current day
    const dayPricingHours = utilityServiceHours.filter((hour: any) => 
      hour.days && hour.days.includes(dbDayOfWeek)
    );

    if (dayPricingHours.length === 0) {
      // Create fallback time slots from 08:00 to 20:00
      for (let hour = 8; hour < 20; hour++) {
        const hourString = hour.toString().padStart(2, '0') + ':00';
        
        // Use a default price based on time period
        let defaultPrice = 50000; // Morning price
        if (hour >= 17) {
          defaultPrice = 60000; // Evening price
        }
        
        timeSlots.push({
          hour: hourString,
          isAvailable: true,
          isBooked: false,
          isSelected: false,
          price: defaultPrice
        });
      }
      
      setTimeSliderData(timeSlots);
      return;
    }

    // Find the overall start and end times from pricing data
    const allStartHours = dayPricingHours.map((sh: any) => parseInt(sh.startTime.split(':')[0]));
    const allEndHours = dayPricingHours.map((sh: any) => parseInt(sh.endTime.split(':')[0]));
    const overallStartHour = Math.min(...allStartHours);
    const overallEndHour = Math.max(...allEndHours);

    for (let hour = overallStartHour; hour < overallEndHour; hour++) {
      const hourString = hour.toString().padStart(2, '0') + ':00';
      
      // Find which pricing hour this specific hour belongs to
      const applicablePricingHour = dayPricingHours.find((sh: any) => {
        const shStart = parseInt(sh.startTime.split(':')[0]);
        const shEnd = parseInt(sh.endTime.split(':')[0]);
        return hour >= shStart && hour < shEnd;
      });
      
      // Check if this hour is booked from the API response
      const isBooked = bookedSlots.some((slot: any) => {
        const slotStart = new Date(slot.startTime).getHours();
        const slotEnd = new Date(slot.endTime).getHours();
        return hour >= slotStart && hour < slotEnd;
      });
      
      // Calculate price using the pricing hour's pricing tiers
      const price = applicablePricingHour ? 
        calculateSlotPrice(applicablePricingHour.pricingTiers || [], 1) : 0;

      timeSlots.push({
        hour: hourString,
        isAvailable: !isBooked && applicablePricingHour !== undefined,
        isBooked: isBooked,
        isSelected: false,
        price: price
      });
    }

    setTimeSliderData(timeSlots);

    // In edit mode, automatically select the time range after setting time slider data
    if (isEdit && formData.startTime && formData.endTime && !selectedTimeRange) {
      // Use setTimeout to ensure timeSliderData state is updated
      setTimeout(() => {
        const startHour = formData.startTime;
        const endHour = formData.endTime;
        
        // Find the start and end indices in the time slider
        const startIndex = timeSlots.findIndex(slot => slot.hour === startHour);
        const endIndex = timeSlots.findIndex(slot => {
          // For end time, we need to find the last hour before endHour
          // Since booking 08:00-10:00 means hours 08:00 and 09:00 are selected
          const endSlotHour = parseInt(endHour.split(':')[0]);
          const slotHour = parseInt(slot.hour.split(':')[0]);
          return slotHour === endSlotHour - 1;
        });
        
        if (startIndex !== -1 && endIndex !== -1) {
          // Update the time slider selection
          const updatedTimeSlots = timeSlots.map((slot, index) => ({
            ...slot,
            isSelected: index >= startIndex && index <= endIndex
          }));
          
          setTimeSliderData(updatedTimeSlots);
          
          // Set the selected time range
          const duration = endIndex - startIndex + 1;
          setSelectedTimeRange({
            startHour: startHour,
            endHour: endHour,
            duration: duration
          });
        }
      }, 100);
    }
  };

  const generateNoBookingTimeSlots = (date: string, availability: any) => {
    if (!availability?.serviceHours) return;

    const availabilityServiceHours = availability.serviceHours as any[];
    const slots: any[] = [];

    availabilityServiceHours.forEach(hour => {
      // Calculate price from this service hour's pricing tiers
      const price = calculateSlotPrice(hour.pricingTiers || [], 1);
      
      // For display in time slot card and availability section
      const totalCapacity = hour.totalCapacity || hour.maxCapacity || 0;
      const bookedSlots = hour.bookedSlots || 0;
      const availableSlots = hour.isUnlimited ? 999 : (hour.availableSlots || 0);

      // In edit mode, if this is the current booking's time slot, ensure correct display
      let displayAvailableSlots = availableSlots;
      if (isEdit && formData.startTime === hour.startTime && formData.endTime === hour.endTime) {
        // For the current booking's time slot in edit mode, make sure we show the correct availability
        // If the API didn't exclude the current booking properly, do it manually
        const currentBookingSlots = parseInt(formData.slots) || 1;
        if (availableSlots === totalCapacity) {
          // API didn't exclude current booking, so we do it manually
          displayAvailableSlots = Math.max(0, totalCapacity - bookedSlots - currentBookingSlots);
        } else {
          // API already excluded current booking
          displayAvailableSlots = availableSlots;
        }
      }

      slots.push({
        startTime: hour.startTime,
        endTime: hour.endTime,
        price, // This comes from the service hour's pricing tiers
        availableSlots: displayAvailableSlots,
        isAvailable: hour.isUnlimited || displayAvailableSlots > 0,
        maxCapacity: totalCapacity,
        bookedSlots: bookedSlots,
        slotType: 'NO_BOOKING_HOUR' // Specific type for NO_BOOKING service hours
      });
    });

    setAvailableTimeSlots(slots);

    // In edit mode, automatically select the time slot after setting slots
    if (isEdit && formData.startTime && formData.endTime && !selectedSlotAvailability) {
      const slot = slots.find(s => 
        s.startTime === formData.startTime && s.endTime === formData.endTime
      );
      if (slot) {
        // Use setTimeout to ensure state is updated
        setTimeout(() => {
          handleTimeSlotSelect(slot);
        }, 50);
      }
    }
  };

  const calculateSlotPrice = (pricingTiers: any[], slots: number) => {
    if (!pricingTiers || pricingTiers.length === 0) return 0;
    
    // Find appropriate pricing tier based on slot threshold
    const tier = pricingTiers
      .sort((a, b) => a.slotThreshold - b.slotThreshold)
      .find(t => slots >= t.slotThreshold) || pricingTiers[0];
    
    return tier.pricePerSlot || 0;
  };

  const handleDateSelect = (date: string) => {
    if (
      selectedUtility &&
      !isDateWithinAdvanceBookingWindow(date, effectiveAdvanceBookingDays, { allowNegativeMin: true })
    ) {
      return;
    }
    setSelectedDate(date);
    setBookingDayCount(1);
    setMultiDaySlotBlocked(null);
    setFormData((prev) => ({ ...prev, bookingDate: date, startTime: '', endTime: '' }));
    setSelectedTimeSlot('');
    const offset = daysOffsetFromToday(date);
    if (offset !== null) {
      setManualDayOffset(String(offset));
    }
    setManualDateText(formatIsoDateToDdMmYyyy(date));
  };

  const advanceBookingBounds = effectiveAdvanceBookingDays;

  const applyManualDayOffset = useCallback(() => {
    if (!advanceBookingBounds || manualDayOffset === '') return;
    const offset = parseInt(manualDayOffset, 10);
    if (Number.isNaN(offset)) return;
    if (offset < advanceBookingBounds.min || offset > advanceBookingBounds.max) return;
    handleDateSelect(dateFromDaysOffset(offset));
  }, [advanceBookingBounds, manualDayOffset, selectedUtility]);

  const applyManualDateText = useCallback(() => {
    if (!manualDateText.trim()) return;
    const iso = parseDdMmYyyyToIsoDate(manualDateText);
    if (!iso) return;
    if (
      selectedUtility &&
      !isDateWithinAdvanceBookingWindow(iso, effectiveAdvanceBookingDays, { allowNegativeMin: true })
    ) {
      return;
    }
    handleDateSelect(iso);
  }, [manualDateText, selectedUtility]);

  const scrollDayStrip = (direction: 'left' | 'right') => {
    dayScrollerRef.current?.scrollBy({
      left: direction === 'left' ? -240 : 240,
      behavior: 'smooth',
    });
  };

  const handleDayScrollerPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).closest('[data-booking-date]')) {
      return;
    }
    if (!dayScrollerRef.current) return;
    dayDragRef.current = {
      active: true,
      moved: false,
      suppressClick: false,
      startX: e.clientX,
      scrollLeft: dayScrollerRef.current.scrollLeft,
    };
    dayScrollerRef.current.setPointerCapture(e.pointerId);
  };

  const handleDayScrollerPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dayDragRef.current.active || !dayScrollerRef.current) return;
    const dx = e.clientX - dayDragRef.current.startX;
    if (Math.abs(dx) > 4) {
      dayDragRef.current.moved = true;
    }
    dayScrollerRef.current.scrollLeft = dayDragRef.current.scrollLeft - dx;
  };

  const handleDayScrollerPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    dayDragRef.current.suppressClick = dayDragRef.current.moved;
    dayDragRef.current.active = false;
    dayDragRef.current.moved = false;
    dayScrollerRef.current?.releasePointerCapture(e.pointerId);
  };

  useEffect(() => {
    if (!selectedDate) return;
    const offset = daysOffsetFromToday(selectedDate);
    if (offset !== null) {
      setManualDayOffset(String(offset));
    }
    setManualDateText(formatIsoDateToDdMmYyyy(selectedDate));
  }, [selectedDate]);

  useEffect(() => {
    if (!selectedDate || !dayScrollerRef.current) return;
    const chip = dayScrollerRef.current.querySelector(`[data-booking-date="${selectedDate}"]`);
    chip?.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
  }, [selectedDate, availableDays]);

  const handleTimeSlotSelect = (slot: any) => {
    if (!slot.isAvailable) return;
    
    const slotKey = `${slot.startTime}-${slot.endTime}`;
    setSelectedTimeSlot(slotKey);
    
    // Store only the time portion (HH:MM format) for later combination with date
    setFormData(prev => ({
      ...prev,
      startTime: slot.startTime,
      endTime: slot.endTime
    }));

    // Find and set the selected slot's availability info
    if (availabilityInfo?.serviceHours) {
      const selectedServiceHour = availabilityInfo.serviceHours.find((hour: any) => 
        hour.startTime === slot.startTime && hour.endTime === slot.endTime
      );
      
      if (selectedServiceHour) {
        let displayBookedSlots, displayAvailableSlots;

        // In both edit and new booking mode, show the API response directly
        // The API already handles the exclusion of current booking in edit mode
        displayBookedSlots = selectedServiceHour.bookedSlots || 0;
        displayAvailableSlots = selectedServiceHour.availableSlots;
        
        // In edit mode, add back the current booking slots to show the total booked
        if (isEdit) {
          displayBookedSlots = displayBookedSlots + (parseInt(formData.slots) || 1);
        }

        // For the availability section, we need to use the corrected values
        let correctedAvailableSlots = displayAvailableSlots;
        let correctedBookedSlots = displayBookedSlots;
        
        if (isEdit) {
          // In edit mode, manually calculate the correct values for the availability section
          const totalCapacity = selectedServiceHour.totalCapacity || selectedServiceHour.maxCapacity || 0;
          const currentBookingSlots = parseInt(formData.slots) || 1;
          
          // If API didn't exclude current booking properly, fix it
          if (selectedServiceHour.availableSlots === totalCapacity && selectedServiceHour.bookedSlots === 0) {
            correctedBookedSlots = currentBookingSlots;
            correctedAvailableSlots = totalCapacity - currentBookingSlots;
          } else {
            correctedBookedSlots = (selectedServiceHour.bookedSlots || 0) + currentBookingSlots;
            correctedAvailableSlots = selectedServiceHour.availableSlots || 0;
          }
        }

        const apiAvailable = selectedServiceHour.isUnlimited
          ? null
          : (selectedServiceHour.availableSlots ?? 0);
        const apiBooked = selectedServiceHour.bookedSlots || 0;

        setSelectedSlotAvailability({
          totalCapacity: selectedServiceHour.totalCapacity || selectedServiceHour.maxCapacity,
          bookedSlots: correctedBookedSlots,
          availableSlots: correctedAvailableSlots,
          isUnlimited: selectedServiceHour.isUnlimited || false,
          originalAvailableSlots: apiAvailable,
          excludedBookedSlots: apiBooked,
          currentOccupancy: apiBooked,
          isAvailable:
            selectedServiceHour.isUnlimited ||
            (apiAvailable !== null && apiAvailable > 0),
        });
      }
    }
  };

  // Time slider functions for TIME_BASED utilities
  const handleTimeSliderMouseDown = (hour: string, event: React.MouseEvent) => {
    event.preventDefault();
    const hourData = timeSliderData.find(h => h.hour === hour);
    if (!hourData || !hourData.isAvailable) return;

    setIsDragging(true);
    setDragStart(hour);
    updateTimeSelection(hour, hour);
  };

  const handleTimeSliderMouseMove = (hour: string, event: React.MouseEvent) => {
    if (!isDragging || !dragStart) return;
    
    const hourData = timeSliderData.find(h => h.hour === hour);
    if (!hourData || !hourData.isAvailable) return;

    updateTimeSelection(dragStart, hour);
  };

  const handleTimeSliderMouseUp = () => {
    setIsDragging(false);
    setDragStart('');
  };

  const updateTimeSelection = (startHour: string, endHour: string) => {
    const startIndex = timeSliderData.findIndex(h => h.hour === startHour);
    const endIndex = timeSliderData.findIndex(h => h.hour === endHour);
    
    if (startIndex === -1 || endIndex === -1) return;

    const actualStart = Math.min(startIndex, endIndex);
    const actualEnd = Math.max(startIndex, endIndex);
    
    // Check if all hours in the range are available
    const rangeHours = timeSliderData.slice(actualStart, actualEnd + 1);
    const allAvailable = rangeHours.every(h => h.isAvailable);
    
    if (!allAvailable) return;

    // Update selection state
    const updatedTimeSlots = timeSliderData.map((slot, index) => ({
      ...slot,
      isSelected: index >= actualStart && index <= actualEnd
    }));
    
    setTimeSliderData(updatedTimeSlots);

    // Calculate duration and set form data
    const duration = actualEnd - actualStart + 1;
    const startTime = timeSliderData[actualStart].hour;
    const endTime = addHoursToTime(timeSliderData[actualEnd].hour, 1);
    
    setSelectedTimeRange({
      startHour: startTime,
      endHour: endTime,
      duration: duration
    });

    // Update form data
    setFormData(prev => ({
      ...prev,
      startTime: startTime,
      endTime: endTime,
      slots: duration.toString() // For TIME_BASED, slots represent hours
    }));
  };

  const addHoursToTime = (timeString: string, hours: number): string => {
    const [hour, minute] = timeString.split(':').map(Number);
    const newHour = hour + hours;
    return `${newHour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
  };

  const clearTimeSelection = () => {
    setTimeSliderData(prev => prev.map(slot => ({ ...slot, isSelected: false })));
    setSelectedTimeRange(null);
    setFormData(prev => ({
      ...prev,
      startTime: '',
      endTime: '',
      slots: '1'
    }));
  };

  const calculatePricing = async () => {
    if (!formData.utilityId || !formData.slots) return;
    
    try {
      const numberOfSlots = parseInt(formData.slots);
      if (isNaN(numberOfSlots) || numberOfSlots < 1) return;

      // Get utility details
      const utility = utilities.find(u => u.id === formData.utilityId);
      if (!utility) return;

      setSelectedUtility(utility);

      // Set deposit amount (preserve from database in edit mode, but allow updates)
      const currentDeposit = utility.depositAmount || 0;
      setDepositAmount(currentDeposit);

      let baseCost = 0;

      const utilityType = utility.group?.utilityType;

      if (utilityType === 'NOTIFICATION_ONLY') {
        baseCost = 0;
      } else if (utilityType === 'TIME_BASED' && selectedTimeRange) {
        const selectedHours = timeSliderData.filter(h => h.isSelected);
        baseCost = selectedHours.reduce((total, hour) => total + hour.price, 0);
      } else if (utilityType !== 'NO_BOOKING') {
        const response = await buildingUtilitiesApi.calculateBookingCost(formData.utilityId, numberOfSlots);
        baseCost = response.totalCost || 0;
      }
      
      setCalculatedCost(baseCost);

      // Persist service fee only — deposit is stored separately
      setFormData(prev => ({
        ...prev,
        totalCost: baseCost.toString(),
      }));

    } catch (err) {
      console.error('Error calculating pricing:', err);
    }
  };

  const loadBuildings = async () => {
    try {
      const response = await buildingsApi.getBuildings();
      if (response && Array.isArray(response)) {
        setBuildings(response);
        // Auto-select user's building if available and no building specified
        if (!buildingIdFromQuery && user?.buildingId) {
          setFormData(prev => ({ ...prev, buildingId: user.buildingId! }));
        }
      }
    } catch (err: any) {
      setError('Error loading buildings');
    }
  };

  const loadApartments = async () => {
    if (!formData.buildingId) return;
    
    try {
      const response = await apartmentsApi.getApartments();
      if (response && Array.isArray(response)) {
        // Filter apartments by building ID
        const filteredApartments = response.filter(apt => apt.buildingId === formData.buildingId);
        setApartments(filteredApartments);
      }
    } catch (err: any) {
      console.error('Error loading apartments:', err);
    }
  };

  const loadApartmentMembers = async () => {
    if (!formData.apartmentId) {
      setApartmentMembers([]);
      return;
    }
    
    try {
      // Use the proper API base URL
      const apiBase = process.env.REACT_APP_API_URL || `${window.location.protocol}//${window.location.hostname}:3000/api`;
      const response = await fetch(`${apiBase}/apartments/${formData.apartmentId}/members`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const members = await response.json();
        setApartmentMembers(members);
        
        // Auto-select the primary member if available
        const primaryMember = members.find((member: ApartmentMember) => member.isPrimary);
        if (primaryMember && !formData.apartmentMemberId) {
          setFormData(prev => ({ ...prev, apartmentMemberId: primaryMember.id }));
        }
      } else {
        console.error('Failed to load apartment members:', response.statusText);
        setApartmentMembers([]);
      }
    } catch (err: any) {
      console.error('Error loading apartment members:', err);
      setApartmentMembers([]);
    }
  };

  const loadUtilityGroups = async () => {
    if (!formData.buildingId) return;
    
    try {
      const response = await buildingUtilityApi.getUtilityGroups(formData.buildingId, {
        registrationOnly: true,
        limit: 100,
      });
      if (response && Array.isArray(response.data)) {
        setUtilityGroups(response.data.filter((g) => g.status === 'ACTIVE'));
      }
    } catch (err: any) {
      console.error('Error loading utility groups:', err);
    }
  };

  const loadUtilities = async () => {
    if (!formData.buildingId || !formData.groupId) return;
    
    try {
      const response = await buildingUtilityApi.getUtilities(formData.buildingId, {
        groupId: formData.groupId,
        limit: 100,
        registrationOnly: true,
      });
      if (response && Array.isArray(response.data)) {
        setUtilities(response.data.filter((utility) => utility.isActive));
      } else {
        setUtilities([]);
      }
    } catch (err: any) {
      console.error('Error loading utilities:', err);
      setUtilities([]);
    }
  };

  const loadBooking = async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      const booking = await buildingUtilityBookingsApi.getBooking(id);
      
      // Helper function to extract time from datetime string
      const extractTime = (dateTimeString: string | null) => {
        if (!dateTimeString) return '';
        try {
          // Create a Date object from the UTC string and convert to local time
          const date = new Date(dateTimeString);
          const hours = date.getHours().toString().padStart(2, '0');
          const minutes = date.getMinutes().toString().padStart(2, '0');
          return `${hours}:${minutes}`;
        } catch {
          return '';
        }
      };
      
      setFormData({
        apartmentId: booking.apartmentId,
        apartmentMemberId: booking.apartmentMemberId,
        utilityId: booking.utilityId,
        groupId: booking.groupId,
        buildingId: booking.buildingId,
        bookingDate: booking.bookingDate.split('T')[0], // Extract date part
        startTime: extractTime(booking.startTime),
        endTime: extractTime(booking.endTime),
        status: booking.status,
        slots: booking.numberOfSlots?.toString() || '1',
        totalCost: booking.totalCost?.toString() || '',
        notes: booking.notes || ''
      });

      // Set selected date and time slot for edit mode
      const startIso = booking.bookingDate.split('T')[0];
      setSelectedDate(startIso);
      const endIso = booking.bookingEndDate?.split('T')[0];
      if (endIso && endIso !== startIso) {
        setBookingDayCount(countInclusiveDays(startIso, endIso));
      } else {
        setBookingDayCount(1);
      }
      const timeSlotKey = `${extractTime(booking.startTime)}-${extractTime(booking.endTime)}`;
      setSelectedTimeSlot(timeSlotKey);

      // Set initial values for edit mode - these will be recalculated when calculatePricing runs
      const serviceFee = resolveUtilityBookingServiceFee(booking);
      setCalculatedCost(serviceFee);
      setDepositAmount(booking.depositAmount || 0);
      setFormData((prev) => ({
        ...prev,
        totalCost: serviceFee.toString(),
      }));

      setLogsLoading(true);
      try {
        const logs = await buildingUtilityApi.getBookingActivityLogs(id);
        setActivityLogs(Array.isArray(logs) ? logs : []);
      } catch (logError) {
        console.error('Error loading booking activity logs:', logError);
        setActivityLogs([]);
      } finally {
        setLogsLoading(false);
      }
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Không tải được thông tin đăng ký'));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;

    // Reset dependent fields when parent changes (single update so groupId/buildingId are not lost)
    if (name === 'buildingId') {
      setFormData((prev) => ({
        ...prev,
        buildingId: value,
        apartmentId: '',
        apartmentMemberId: '',
        groupId: '',
        utilityId: '',
      }));
      setApartments([]);
      setApartmentMembers([]);
      setUtilityGroups([]);
      setUtilities([]);
    } else if (name === 'apartmentId') {
      setFormData((prev) => ({ ...prev, apartmentId: value, apartmentMemberId: '' }));
      setApartmentMembers([]);
    } else if (name === 'groupId') {
      setFormData((prev) => ({ ...prev, groupId: value, utilityId: '' }));
      setUtilities([]);
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }

    if (name === 'slots' && selectedUtility?.group?.utilityType === 'NO_BOOKING' && selectedDate) {
      // Re-check availability when number of people changes for NO_BOOKING utilities
      // Add a small delay to ensure the form data is updated
      setTimeout(() => {
        checkAvailabilityForDate(selectedDate);
      }, 100);
    }
  };

  const getStatusLabel = (status: BookingStatus) =>
    getUtilityBookingStatusLabel(status, lang === 'en' ? 'en' : 'vi');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isReadOnly) {
      navigate('/building-utility-bookings');
      return;
    }
    
    if (!formData.buildingId) {
      setError('Vui lòng chọn tòa nhà');
      return;
    }

    if (!formData.apartmentId) {
      setError('Vui lòng chọn căn hộ');
      return;
    }

    if (!formData.utilityId) {
      setError('Vui lòng chọn tiện ích');
      return;
    }

    if (!formData.bookingDate) {
      setError('Vui lòng chọn ngày đặt');
      return;
    }

    if (
      selectedUtility?.group?.utilityType === 'FIXED_SLOTS' ||
      selectedUtility?.group?.utilityType === 'NOTIFICATION_ONLY'
    ) {
      if (!formData.startTime || !formData.endTime) {
        setError('Vui lòng chọn khung giờ');
        return;
      }
      if (
        selectedUtility?.group?.utilityType === 'NOTIFICATION_ONLY' ||
        selectedUtility?.group?.utilityType === 'FIXED_SLOTS'
      ) {
        if (multiDaySlotBlocked) {
          setError(multiDaySlotBlocked);
          return;
        }
        if (selectedUtility?.group?.utilityType === 'NOTIFICATION_ONLY' && selectedSlotAvailability && !selectedSlotAvailability.isUnlimited) {
          const remaining = getBookableSlotsRemaining(selectedSlotAvailability);
          const requested = parseInt(formData.slots, 10) || 1;
          if (remaining < requested) {
            setError('Khung giờ này đã có đăng ký — không thể tạo thêm.');
            return;
          }
        }
        if (selectedUtility?.group?.utilityType === 'FIXED_SLOTS' && selectedSlotAvailability && !selectedSlotAvailability.isUnlimited) {
          const remaining = getBookableSlotsRemaining(selectedSlotAvailability);
          const requested = parseInt(formData.slots, 10) || 1;
          if (requested > remaining) {
            setError(`Số slot vượt quá sức chứa còn lại (${remaining} slot)`);
            return;
          }
        }
      }
    }

    if (selectedUtility?.group?.utilityType === 'TIME_BASED') {
      if (!selectedTimeRange) {
        setError('Vui lòng chọn khoảng thời gian');
        return;
      }
    }

    if (selectedUtility?.group?.utilityType === 'NO_BOOKING') {
      if (!formData.startTime || !formData.endTime) {
        setError('Vui lòng chọn khung giờ sử dụng');
        return;
      }
      
      if (!selectedSlotAvailability) {
        setError('Vui lòng chọn khung giờ sử dụng');
        return;
      }
      
      const requestedSlots = parseInt(formData.slots) || 1;
      const remaining = getBookableSlotsRemaining(selectedSlotAvailability);
      if (!selectedSlotAvailability.isUnlimited && requestedSlots > remaining) {
        setError(`Số người vượt quá sức chứa còn lại (${remaining} người)`);
        return;
      }
    }

    try {
      setLoading(true);
      setError(null);

      // Helper function to combine date and time
      const combineDateTime = (date: string, time: string) => {
        if (!date || !time) return undefined;
        // Create a proper local datetime and convert to ISO string
        const localDateTime = new Date(`${date}T${time}:00`);
        return localDateTime.toISOString();
      };

      // Calculate final cost for TIME_BASED utilities
      let finalTotalCost = formData.totalCost ? parseFloat(formData.totalCost) : undefined;
      
      if (selectedUtility?.group?.utilityType === 'TIME_BASED' && selectedTimeRange) {
        const selectedHours = timeSliderData.filter(h => h.isSelected);
        finalTotalCost = selectedHours.reduce((total, hour) => total + hour.price, 0);
      } else if (selectedUtility?.group?.utilityType === 'NOTIFICATION_ONLY') {
        finalTotalCost = 0;
      }

      const multiDayEndDateIso =
        supportsConsecutiveDayBooking && bookingDayCount > 1
          ? bookingEndDateIso
          : formData.bookingDate;

      const submitData = {
        apartmentId: formData.apartmentId,
        apartmentMemberId: formData.apartmentMemberId,
        utilityId: formData.utilityId,
        groupId: formData.groupId,
        buildingId: formData.buildingId,
        bookingDate: formData.bookingDate,
        bookingEndDate:
          supportsConsecutiveDayBooking && bookingDayCount > 1
            ? bookingEndDateIso
            : undefined,
        startTime: selectedUtility?.group?.utilityType === 'TIME_BASED' && selectedTimeRange
          ? combineDateTime(formData.bookingDate, selectedTimeRange.startHour)
          : combineDateTime(formData.bookingDate, formData.startTime),
        endTime: selectedUtility?.group?.utilityType === 'TIME_BASED' && selectedTimeRange
          ? combineDateTime(formData.bookingDate, selectedTimeRange.endHour)
          : combineDateTime(multiDayEndDateIso, formData.endTime),
        status: formData.status,
        numberOfSlots: selectedUtility?.group?.utilityType === 'TIME_BASED' && selectedTimeRange
          ? selectedTimeRange.duration
          : (formData.slots ? parseInt(formData.slots) : 1),
        totalCost: selectedUtility?.group?.utilityType === 'NO_BOOKING' 
          ? (selectedSlotAvailability && availableTimeSlots.length > 0 
              ? (() => {
                  const selectedSlot = availableTimeSlots.find(s => 
                    s.startTime === formData.startTime && s.endTime === formData.endTime
                  );
                  const slotPrice = selectedSlot?.price || 0;
                  const numberOfPeople = parseInt(formData.slots) || 1;
                  return slotPrice * numberOfPeople;
                })()
              : 0)
          : finalTotalCost,
        depositAmount: selectedUtility?.group?.utilityType === 'NO_BOOKING' 
          ? 0 // NO_BOOKING utilities typically don't require deposit
          : depositAmount,
        notes: formData.notes || undefined,
      };

      if (isEdit) {
        const updatePayload = {
          apartmentMemberId: submitData.apartmentMemberId || undefined,
          bookingDate: submitData.bookingDate,
          bookingEndDate: submitData.bookingEndDate,
          startTime: submitData.startTime,
          endTime: submitData.endTime,
          status: submitData.status,
          numberOfSlots: submitData.numberOfSlots,
          totalCost: submitData.totalCost,
          depositAmount: submitData.depositAmount,
          notes: submitData.notes,
        };
        await buildingUtilityBookingsApi.updateBooking(id!, updatePayload);
      } else {
        await buildingUtilityBookingsApi.createBooking(submitData);
      }

      navigate('/building-utility-bookings');
    } catch (err: any) {
      setError(
        getApiErrorMessage(
          err,
          isEdit ? 'Không cập nhật được đăng ký' : 'Không tạo được đăng ký mới',
        ),
      );
    } finally {
      setLoading(false);
    }
  };

  if (loading && isEdit) {
    return (
      <div className="max-w-4xl mx-auto py-8">
        <div className="text-center">
          <div className="text-lg text-gray-600">Đang tải thông tin đăng ký...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto py-8">
      {/* Header */}
      <div className="mb-8">
        <nav className="text-sm text-gray-500 mb-4">
          <span>Tiện ích tòa nhà</span> / 
          <span className="text-gray-900 ml-1">
            {isEdit ? (isReadOnly ? 'Xem đăng ký' : 'Chỉnh sửa đăng ký') : 'Tạo đăng ký'}
          </span>
        </nav>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          {isEdit ? (isReadOnly ? 'Xem đăng ký' : 'Chỉnh sửa đăng ký') : 'Tạo đăng ký mới'}
        </h1>
        <p className="text-gray-600">
          {isEdit
            ? (isReadOnly ? 'Xem thông tin đăng ký' : 'Cập nhật thông tin đăng ký')
            : 'Tạo đăng ký sử dụng tiện ích mới'}
        </p>
      </div>

      {isReadOnly && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-sm text-blue-800">
          Đăng ký đã hoàn thành hoặc đã hủy — chỉ xem, không thể chỉnh sửa.
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-red-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-red-800">{error}</span>
          </div>
        </div>
      )}

      {/* Form */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <fieldset disabled={isReadOnly} className="space-y-6 border-0 p-0 m-0 min-w-0">
          {/* Building Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tòa nhà <span className="text-red-500">*</span>
            </label>
            <select
              name="buildingId"
              value={formData.buildingId}
              onChange={handleInputChange}
              disabled={isEdit}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100"
              required
            >
              <option value="">-- Chọn tòa nhà --</option>
              {buildings.map((building) => (
                <option key={building.id} value={building.id}>
                  {building.name}
                </option>
              ))}
            </select>
          </div>

          {/* Apartment Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Căn hộ <span className="text-red-500">*</span>
            </label>
            <select
              name="apartmentId"
              value={formData.apartmentId}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            >
              <option value="">-- Chọn căn hộ --</option>
              {apartments.map((apartment) => (
                <option key={apartment.id} value={apartment.id}>
                  {apartment.code} - {apartment.ownerName}
                </option>
              ))}
            </select>
          </div>

          {/* Apartment Member Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Thành viên căn hộ <span className="text-red-500">*</span>
            </label>
            <select
              name="apartmentMemberId"
              value={formData.apartmentMemberId}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            >
              <option value="">-- Chọn thành viên căn hộ --</option>
              {apartmentMembers.map((member) => (
                <option key={member.id} value={member.id}>
                  {member.fullName}
                </option>
              ))}
            </select>
          </div>

          {/* Utility Group Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nhóm tiện ích <span className="text-red-500">*</span>
            </label>
            <select
              name="groupId"
              value={formData.groupId}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            >
              <option value="">-- Chọn nhóm tiện ích --</option>
              {utilityGroups.map((group) => (
                <option key={group.id} value={group.id}>
                  {group.icon} {group.name}
                </option>
              ))}
            </select>
          </div>

          {/* Utility Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tiện ích <span className="text-red-500">*</span>
            </label>
            <select
              name="utilityId"
              value={formData.utilityId}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            >
              <option value="">-- Chọn tiện ích --</option>
              {utilities.map((utility) => (
                <option key={utility.id} value={utility.id}>
                  {utility.name} {utility.location && `(${utility.location})`}
                </option>
              ))}
            </select>
          </div>

          {/* Movie-style Date Selection */}
          <div className="mb-8">
            <label className="block text-lg font-medium text-gray-900 mb-4">
              📅 Chọn ngày đặt <span className="text-red-500">*</span>
            </label>
            
            {selectedUtility && (
              <p className="text-sm text-gray-600 mb-3">
                Có thể đặt từ{' '}
                <span className="font-medium">
                  {effectiveAdvanceBookingDays?.min ?? 0}
                </span>{' '}
                đến{' '}
                <span className="font-medium">
                  {effectiveAdvanceBookingDays?.max ?? 7}
                </span>{' '}
                ngày kể từ hôm nay. Chỉnh khoảng này trong mục chỉnh sửa tiện ích → Cài đặt đặt trước.
              </p>
            )}

            {/* Day quick picker — kéo ngang hoặc dùng nút ‹ › */}
            <div className="flex items-center gap-2 mb-4">
              <button
                type="button"
                onClick={() => scrollDayStrip('left')}
                className="flex-shrink-0 p-2 rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
                aria-label="Cuộn ngày trước"
              >
                ‹
              </button>
              <div
                ref={dayScrollerRef}
                className="flex flex-1 space-x-3 overflow-x-auto pb-2 scroll-smooth cursor-grab active:cursor-grabbing touch-pan-x"
                style={{ scrollbarWidth: 'thin' }}
                onPointerDown={handleDayScrollerPointerDown}
                onPointerMove={handleDayScrollerPointerMove}
                onPointerUp={handleDayScrollerPointerUp}
                onPointerCancel={handleDayScrollerPointerUp}
              >
                {availableDays.map((day) => (
                  <button
                    key={day.date}
                    type="button"
                    data-booking-date={day.date}
                    onClick={() => handleDateSelect(day.date)}
                    className={`
                      flex-shrink-0 p-4 rounded-xl border-2 text-center min-w-[80px] transition-all duration-200
                      ${selectedDate === day.date
                        ? 'border-blue-500 bg-blue-50 text-blue-700 shadow-md'
                        : 'border-gray-200 bg-white text-gray-700 hover:border-blue-300 hover:bg-blue-50'
                      }
                      ${day.isPast ? 'opacity-50 cursor-not-allowed' : ''}
                    `}
                    disabled={day.isPast}
                  >
                    <div className="text-xs font-medium mb-1">{day.dayName}</div>
                    <div className="text-lg font-bold">{day.dayNumber}</div>
                    <div className="text-xs text-gray-500">{day.month}</div>
                    {day.isToday && (
                      <div className="text-xs text-blue-600 font-medium mt-1">Hôm nay</div>
                    )}
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={() => scrollDayStrip('right')}
                className="flex-shrink-0 p-2 rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
                aria-label="Cuộn ngày sau"
              >
                ›
              </button>
            </div>
            <p className="text-xs text-gray-500 mb-4">
              Kéo ngang trên dãy ngày hoặc bấm ‹ › để xem các ngày tiếp theo.
            </p>

            {bookingDateMin && bookingDateMax && advanceBookingBounds && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Hoặc chọn ngày trong khoảng cho phép
                </label>
                <div className="flex flex-wrap items-end gap-4">
                  <div className="w-36">
                    <label className="block text-xs text-gray-600 mb-1">
                      Số ngày kể từ hôm nay
                    </label>
                    <input
                      type="number"
                      value={manualDayOffset}
                      min={advanceBookingBounds.min}
                      max={advanceBookingBounds.max}
                      onChange={(e) => setManualDayOffset(e.target.value)}
                      onBlur={applyManualDayOffset}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          applyManualDayOffset();
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder={`${advanceBookingBounds.min}–${advanceBookingBounds.max}`}
                    />
                  </div>
                  <div className="w-40">
                    <label className="block text-xs text-gray-600 mb-1">
                      Ngày (dd/mm/yyyy)
                    </label>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={manualDateText}
                      onChange={(e) => setManualDateText(e.target.value)}
                      onBlur={applyManualDateText}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          applyManualDateText();
                        }
                      }}
                      placeholder="dd/mm/yyyy"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Chọn trên lịch</label>
                    <input
                      type="date"
                      value={selectedDate || formData.bookingDate}
                      min={bookingDateMin}
                      max={bookingDateMax}
                      onChange={(e) => {
                        if (e.target.value) {
                          handleDateSelect(e.target.value);
                        }
                      }}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>
                </div>
              </div>
            )}

            {supportsConsecutiveDayBooking && selectedDate && (
              <div className="mb-4 p-4 rounded-lg border border-purple-100 bg-purple-50/50">
                <label className="block text-sm font-medium text-gray-800 mb-2">
                  Số ngày sử dụng liên tiếp
                </label>
                <div className="flex flex-wrap items-center gap-3">
                  <select
                    value={bookingDayCount}
                    onChange={(e) => {
                      const n = parseInt(e.target.value, 10) || 1;
                      setBookingDayCount(n);
                      setMultiDaySlotBlocked(null);
                    }}
                    className="rounded-lg border border-gray-300 px-3 py-2 text-sm bg-white"
                  >
                    {Array.from({ length: maxConsecutiveDaysFromStart }, (_, i) => i + 1).map(
                      (n) => (
                        <option key={n} value={n}>
                          {n} ngày
                        </option>
                      ),
                    )}
                  </select>
                  {bookingDayCount > 1 && bookingEndDateIso && (
                    <span className="text-sm text-purple-800 font-medium">
                      {formatIsoDateToDdMmYyyy(selectedDate)} →{' '}
                      {formatIsoDateToDdMmYyyy(bookingEndDateIso)}
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-600 mt-2">
                  Ví dụ tiệc cưới 2–3 ngày: chọn ngày bắt đầu, sau đó chọn số ngày. Hệ thống kiểm tra
                  trống từng ngày với cùng khung giờ.
                </p>
                {multiDaySlotBlocked && (
                  <p className="text-sm text-red-600 mt-2">⚠️ {multiDaySlotBlocked}</p>
                )}
              </div>
            )}
            
            {loadingAvailability && (
              <div className="flex items-center justify-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                <span className="ml-2 text-sm text-blue-600">Đang kiểm tra tình trạng...</span>
              </div>
            )}
          </div>

          {/* Time Slot Selection - NO_BOOKING */}
          {selectedDate && selectedUtility?.group?.utilityType === 'NO_BOOKING' && availableTimeSlots.length > 0 && (
            <div className="mb-8">
              <label className="block text-lg font-medium text-gray-900 mb-4">
                📚 Chọn khung giờ sử dụng <span className="text-red-500">*</span>
              </label>
              
              {/* Time Slots Grid */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {availableTimeSlots.map((slot, index) => (
                  <button
                    key={`${slot.startTime}-${slot.endTime}`}
                    type="button"
                    onClick={() => handleTimeSlotSelect(slot)}
                    disabled={!slot.isAvailable}
                    className={`
                      p-3 rounded-lg border-2 text-center transition-all duration-200
                      ${selectedTimeSlot === `${slot.startTime}-${slot.endTime}`
                        ? 'border-blue-500 bg-blue-50 text-blue-700 shadow-md'
                        : slot.isAvailable
                          ? 'border-gray-200 bg-white text-gray-700 hover:border-blue-300 hover:bg-blue-50'
                          : 'border-red-200 bg-red-50 text-red-400 cursor-not-allowed'
                      }
                    `}
                  >
                    <div className="font-medium text-sm">
                      {slot.startTime} - {slot.endTime}
                    </div>
                    <div className="text-xs mt-1">
                      {slot.isAvailable ? (
                        <>
                          <div className="text-green-600">
                            {slot.availableSlots === 999 ? 'Không giới hạn' : `${slot.availableSlots} người`}
                          </div>
                          {slot.price > 0 && (
                            <div className="text-blue-600 font-medium">
                              {slot.price.toLocaleString('vi-VN')}đ/người
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="text-red-500">Đã đầy</div>
                      )}
                    </div>
                  </button>
                ))}
              </div>
              
              {availableTimeSlots.length === 0 && !loadingAvailability && (
                <div className="text-center py-8 text-gray-500">
                  <div className="text-4xl mb-2">😔</div>
                  <p>Không có khung giờ nào khả dụng cho ngày này</p>
                </div>
              )}
            </div>
          )}

          {/* Time Slot Selection - FIXED_SLOTS & NOTIFICATION_ONLY (phòng sinh hoạt, đăng ký có khung giờ) */}
          {selectedDate &&
            (selectedUtility?.group?.utilityType === 'FIXED_SLOTS' ||
              selectedUtility?.group?.utilityType === 'NOTIFICATION_ONLY') &&
            availableTimeSlots.length > 0 && (
            <div className="mb-8">
              <label className="block text-lg font-medium text-gray-900 mb-4">
                🕐 Chọn khung giờ <span className="text-red-500">*</span>
              </label>
              
              {/* Time Slots Grid */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {availableTimeSlots.map((slot, index) => (
                  <button
                    key={`${slot.startTime}-${slot.endTime}`}
                    type="button"
                    onClick={() => handleTimeSlotSelect(slot)}
                    disabled={!slot.isAvailable}
                    className={`
                      p-3 rounded-lg border-2 text-center transition-all duration-200
                      ${selectedTimeSlot === `${slot.startTime}-${slot.endTime}`
                        ? 'border-blue-500 bg-blue-50 text-blue-700 shadow-md'
                        : slot.isAvailable
                          ? 'border-gray-200 bg-white text-gray-700 hover:border-blue-300 hover:bg-blue-50'
                          : 'border-red-200 bg-red-50 text-red-400 cursor-not-allowed'
                      }
                    `}
                  >
                    <div className="font-medium text-sm">
                      {slot.startTime} - {slot.endTime}
                    </div>
                    <div className="text-xs mt-1">
                      {slot.isAvailable ? (
                        <>
                          <div className="text-green-600">
                            {slot.availableSlots === 999
                              ? 'Không giới hạn'
                              : `Còn ${slot.availableSlots} ${slotCapacityUnit}`}
                          </div>
                          <div className="text-blue-600 font-medium">
                            {slot.price.toLocaleString('vi-VN')}đ
                          </div>
                        </>
                      ) : (
                        <div className="text-red-500">Đã đầy</div>
                      )}
                    </div>
                  </button>
                ))}
              </div>
              
              {availableTimeSlots.length === 0 && !loadingAvailability && (
                <div className="text-center py-8 text-gray-500">
                  <div className="text-4xl mb-2">😔</div>
                  <p>Không có khung giờ nào khả dụng cho ngày này</p>
                </div>
              )}
            </div>
          )}

          {selectedDate &&
            selectedUtility?.group?.utilityType === 'NOTIFICATION_ONLY' &&
            !loadingAvailability &&
            availableTimeSlots.length === 0 && (
              <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                Không có khung giờ cho ngày này. Kiểm tra <strong>Thời gian phục vụ</strong> trong
                mục chỉnh sửa tiện ích (đúng thứ trong tuần và giờ bắt đầu/kết thúc).
              </div>
            )}

          {/* Time Slider - TIME_BASED */}
          {selectedDate && selectedUtility?.group?.utilityType === 'TIME_BASED' && (
            timeSliderData.length > 0 ? (
              <div className="mb-8">
                <label className="block text-lg font-medium text-gray-900 mb-4">
                  ⏰ Chọn thời gian <span className="text-red-500">*</span>
                </label>
                
                {/* Time Ruler */}
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="mb-4">
                    <p className="text-sm text-gray-600 mb-2">
                      Kéo và thả để chọn khoảng thời gian. 
                      <span className="text-green-600 ml-2">🟢 Giá thấp</span>
                      <span className="text-orange-600 ml-2">🟠 Giá cao</span>
                      <span className="text-red-600 ml-2">🔴 Đã đặt</span>
                      <span className="text-blue-600 ml-2">🔵 Đã chọn</span>
                    </p>
                    {selectedTimeRange && (
                      <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded p-2">
                        <span className="text-sm font-medium text-blue-800">
                          Đã chọn: {selectedTimeRange.startHour} - {selectedTimeRange.endHour} 
                          ({selectedTimeRange.duration} giờ)
                        </span>
                        <button
                          type="button"
                          onClick={clearTimeSelection}
                          className="text-xs text-blue-600 hover:text-blue-800 underline"
                        >
                          Xóa
                        </button>
                      </div>
                    )}
                  </div>
                  
                  {/* Time Slider */}
                  <div 
                    className="flex border border-gray-300 rounded-lg overflow-hidden"
                    onMouseUp={handleTimeSliderMouseUp}
                    onMouseLeave={handleTimeSliderMouseUp}
                  >
                    {timeSliderData.map((hourSlot, index) => (
                      <div
                        key={hourSlot.hour}
                        className={`
                          flex-1 p-2 text-center cursor-pointer border-r border-gray-200 last:border-r-0 
                          select-none transition-all duration-150 min-w-[60px]
                          ${hourSlot.isSelected 
                            ? 'bg-blue-500 text-white' 
                            : hourSlot.isBooked
                              ? 'bg-red-400 text-white cursor-not-allowed'
                              : hourSlot.isAvailable
                                ? (hourSlot.price >= 60000 
                                    ? 'bg-orange-400 text-white hover:bg-orange-500' 
                                    : 'bg-green-400 text-white hover:bg-green-500')
                                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          }
                        `}
                        onMouseDown={(e) => handleTimeSliderMouseDown(hourSlot.hour, e)}
                        onMouseEnter={(e) => handleTimeSliderMouseMove(hourSlot.hour, e)}
                        title={`${hourSlot.hour} - ${addHoursToTime(hourSlot.hour, 1)} (${hourSlot.price.toLocaleString('vi-VN')}đ/giờ)`}
                      >
                        <div className="text-xs font-medium">
                          {hourSlot.hour}
                        </div>
                        <div className="text-xs mt-1">
                          {hourSlot.price.toLocaleString('vi-VN')}đ
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Hour Labels */}
                  <div className="flex mt-2">
                    {timeSliderData.map((hourSlot, index) => (
                      <div key={`label-${hourSlot.hour}`} className="flex-1 text-center min-w-[60px]">
                        <div className="text-xs text-gray-500">
                          {index % 2 === 0 ? hourSlot.hour.split(':')[0] + 'h' : ''}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="mb-8">
                <label className="block text-lg font-medium text-gray-900 mb-4">
                  ⏰ Chọn thời gian <span className="text-red-500">*</span>
                </label>
                <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="text-4xl mb-2">😔</div>
                  <p>Không có giờ hoạt động nào cho ngày này</p>
                  <p className="text-sm mt-2">Vui lòng chọn ngày khác hoặc kiểm tra lại cấu hình tiện ích</p>
                </div>
              </div>
            )
          )}

          {/* NO_BOOKING Availability Display */}
          {selectedDate && selectedUtility && selectedUtility.group?.utilityType === 'NO_BOOKING' && (selectedSlotAvailability || availabilityInfo) && (
            <div className="mb-8">
              <label className="block text-lg font-medium text-gray-900 mb-4">
                📚 Tình trạng sức chứa
              </label>
              
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                {selectedTimeSlot && selectedSlotAvailability ? (
                  // Show selected time slot availability
                  <>
                    <div className="text-sm text-blue-600 font-medium mb-4">
                      Khung giờ: {formData.startTime} - {formData.endTime}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="text-center">
                        <div className="text-3xl font-bold text-blue-600">
                          {selectedSlotAvailability.isUnlimited ? '∞' : selectedSlotAvailability.totalCapacity}
                        </div>
                        <div className="text-sm text-gray-600">
                          {selectedSlotAvailability.isUnlimited ? 'Không giới hạn' : 'Tổng sức chứa'}
                        </div>
                      </div>
                      
                      <div className="text-center">
                        <div className="text-3xl font-bold text-orange-600">
                          {selectedSlotAvailability.currentOccupancy || 0}
                        </div>
                        <div className="text-sm text-gray-600">Đang sử dụng</div>
                      </div>
                      
                      <div className="text-center">
                        <div className={`text-3xl font-bold ${(() => {
                          if (selectedSlotAvailability.isUnlimited) return 'text-green-600';
                          const requestedSlots = parseInt(formData.slots) || 1;
                          const canBook = (selectedSlotAvailability.availableSlots || 0) >= requestedSlots;
                          return canBook ? 'text-green-600' : 'text-red-600';
                        })()}`}>
                          {selectedSlotAvailability.isUnlimited ? '∞' : (() => {
                            // Show same value as "Tối đa" field - available slots without subtraction
                            return selectedSlotAvailability.availableSlots || 0;
                          })()}
                        </div>
                        <div className="text-sm text-gray-600">
                          {selectedSlotAvailability.isUnlimited ? 'Luôn sẵn sàng' : `Còn trống (sau đăng ký ${formData.slots || 1} người)`}
                        </div>
                      </div>
                    </div>
                  </>
                ) : availabilityInfo?.serviceHours && (
                  // Show overall availability when no time slot selected
                  <>
                    <div className="text-sm text-gray-500 mb-4">
                      Chọn khung giờ để xem tình trạng chi tiết
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="text-center">
                        <div className="text-3xl font-bold text-blue-600">
                          {(() => {
                            const serviceHours = availabilityInfo.serviceHours;
                            const hasUnlimited = serviceHours.some((hour: any) => hour.isUnlimited);
                            if (hasUnlimited) return '∞';
                            const totalCapacity = serviceHours.reduce((sum: number, hour: any) => sum + (hour.totalCapacity || hour.maxCapacity || 0), 0);
                            return totalCapacity;
                          })()}
                        </div>
                        <div className="text-sm text-gray-600">
                          {(() => {
                            const serviceHours = availabilityInfo.serviceHours;
                            const hasUnlimited = serviceHours.some((hour: any) => hour.isUnlimited);
                            return hasUnlimited ? 'Không giới hạn' : 'Tổng sức chứa';
                          })()}
                        </div>
                      </div>
                      
                      <div className="text-center">
                        <div className="text-3xl font-bold text-orange-600">
                          {(() => {
                            const serviceHours = availabilityInfo.serviceHours;
                            return serviceHours.reduce((sum: number, hour: any) => sum + (hour.bookedSlots || 0), 0);
                          })()}
                        </div>
                        <div className="text-sm text-gray-600">Đang sử dụng</div>
                      </div>
                      
                      <div className="text-center">
                        <div className={`text-3xl font-bold ${(() => {
                          const serviceHours = availabilityInfo.serviceHours;
                          const hasUnlimited = serviceHours.some((hour: any) => hour.isUnlimited);
                          if (hasUnlimited) return 'text-green-600';
                          const requestedSlots = parseInt(formData.slots) || 1;
                          const totalAvailable = serviceHours.reduce((sum: number, hour: any) => sum + (hour.availableSlots || 0), 0);
                          const canBook = totalAvailable >= requestedSlots;
                          return canBook ? 'text-green-600' : 'text-red-600';
                        })()}`}>
                          {(() => {
                            const serviceHours = availabilityInfo.serviceHours;
                            const hasUnlimited = serviceHours.some((hour: any) => hour.isUnlimited);
                            if (hasUnlimited) return '∞';
                            const requestedSlots = parseInt(formData.slots) || 1;
                            const totalAvailable = serviceHours.reduce((sum: number, hour: any) => sum + (hour.availableSlots || 0), 0);
                            const simulatedAvailable = Math.max(0, totalAvailable - requestedSlots);
                            return simulatedAvailable;
                          })()}
                        </div>
                        <div className="text-sm text-gray-600">
                          {(() => {
                            const serviceHours = availabilityInfo.serviceHours;
                            const hasUnlimited = serviceHours.some((hour: any) => hour.isUnlimited);
                            return hasUnlimited ? 'Luôn sẵn sàng' : `Còn trống (sau đăng ký ${formData.slots || 1} người)`;
                          })()}
                        </div>
                      </div>
                    </div>
                  </>
                )}
                
                <div className="mt-4 text-center">
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                    (() => {
                      if (selectedTimeSlot && selectedSlotAvailability) {
                        // Show selected slot availability
                        if (selectedSlotAvailability.isUnlimited) return true;
                        const requestedSlots = parseInt(formData.slots) || 1;
                        return (selectedSlotAvailability.availableSlots || 0) >= requestedSlots;
                      } else if (availabilityInfo?.serviceHours) {
                        // Show overall availability from all service hours
                        const serviceHours = availabilityInfo.serviceHours;
                        const hasUnlimited = serviceHours.some((hour: any) => hour.isUnlimited);
                        if (hasUnlimited) return true;
                        const requestedSlots = parseInt(formData.slots) || 1;
                        const totalAvailable = serviceHours.reduce((sum: number, hour: any) => sum + (hour.availableSlots || 0), 0);
                        return totalAvailable >= requestedSlots;
                      }
                      return false;
                    })()
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {(() => {
                      if (selectedTimeSlot && selectedSlotAvailability) {
                        // Show selected slot status
                        if (selectedSlotAvailability.isUnlimited) return '✅ Có thể đăng ký';
                        const requestedSlots = parseInt(formData.slots) || 1;
                        const canBook = (selectedSlotAvailability.availableSlots || 0) >= requestedSlots;
                        return canBook ? '✅ Có thể đăng ký' : '❌ Vượt quá sức chứa';
                      } else if (availabilityInfo?.serviceHours) {
                        // Show overall status from all service hours
                        const serviceHours = availabilityInfo.serviceHours;
                        const hasUnlimited = serviceHours.some((hour: any) => hour.isUnlimited);
                        if (hasUnlimited) return '✅ Có thể đăng ký';
                        const requestedSlots = parseInt(formData.slots) || 1;
                        const totalAvailable = serviceHours.reduce((sum: number, hour: any) => sum + (hour.availableSlots || 0), 0);
                        const canBook = totalAvailable >= requestedSlots;
                        return canBook ? '✅ Có thể đăng ký' : '❌ Vượt quá sức chứa';
                      }
                      return '❓ Chưa có thông tin';
                    })()}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Availability Information */}
          {selectedUtility && formData.bookingDate && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Tình trạng sẵn có</h3>
              
              {(selectedUtility.group?.utilityType === 'FIXED_SLOTS' ||
                selectedUtility.group?.utilityType === 'NOTIFICATION_ONLY') &&
                (selectedSlotAvailability || availabilityInfo) && (
                <div className="space-y-2">
                  {selectedTimeSlot && selectedSlotAvailability ? (
                    // Show selected time slot availability (API counts — not simulated until save)
                    <>
                      <div className="text-sm text-blue-600 font-medium mb-2">
                        Khung giờ: {formData.startTime} - {formData.endTime}
                      </div>
                      {(() => {
                        const capacity =
                          selectedSlotAvailability.totalCapacity ||
                          selectedSlotAvailability.maxCapacity ||
                          0;
                        const booked =
                          selectedSlotAvailability.excludedBookedSlots ??
                          selectedSlotAvailability.bookedSlots ??
                          0;
                        const remaining = selectedSlotAvailability.isUnlimited
                          ? null
                          : (selectedSlotAvailability.originalAvailableSlots ??
                            selectedSlotAvailability.availableSlots ??
                            0);
                        const requested = parseInt(formData.slots, 10) || 1;
                        const canCreate =
                          selectedSlotAvailability.isUnlimited ||
                          (remaining !== null && remaining >= requested);

                        return (
                          <>
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-gray-600">Sức chứa:</span>
                              <span className="font-medium">
                                {selectedSlotAvailability.isUnlimited
                                  ? 'Không giới hạn'
                                  : `${capacity} ${slotCapacityUnit}`}
                              </span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-gray-600">Đã đặt:</span>
                              <span className="font-medium text-orange-600">
                                {booked} {slotCapacityUnit}
                              </span>
                            </div>
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-gray-600">Còn trống:</span>
                              <span
                                className={`font-medium ${
                                  canCreate ? 'text-green-600' : 'text-red-600'
                                }`}
                              >
                                {selectedSlotAvailability.isUnlimited
                                  ? 'Không giới hạn'
                                  : `${remaining} ${slotCapacityUnit}`}
                              </span>
                            </div>
                            {canCreate ? (
                              <div className="bg-green-50 border border-green-200 rounded p-2 mt-2">
                                <p className="text-sm text-green-800">
                                  ✅ Có thể <strong>Tạo mới</strong> đăng ký cho khung giờ này
                                  {!selectedSlotAvailability.isUnlimited && remaining !== null
                                    ? ` (sẽ dùng ${requested} ${slotCapacityUnit}, sau khi lưu còn ${Math.max(0, remaining - requested)}).`
                                    : '.'}
                                </p>
                              </div>
                            ) : (
                              <div className="bg-red-50 border border-red-200 rounded p-2 mt-2">
                                <p className="text-sm text-red-600">
                                  ⚠️ Khung giờ này đã có đăng ký — không thể đặt thêm (ví dụ tiệc cưới).
                                </p>
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </>
                  ) : availabilityInfo?.serviceHours?.length ? (
                    <>
                      <div className="text-sm text-gray-500 mb-2">
                        {isNotificationOnlyUtility
                          ? 'Tình trạng từng khung giờ (mặc định 1 lượt đăng ký/khung):'
                          : 'Chọn khung giờ để xem tình trạng chi tiết'}
                      </div>
                      <div className="space-y-2">
                        {(availabilityInfo.serviceHours as any[]).map((hour) => {
                          const total =
                            hour.totalCapacity ?? hour.maxCapacity ?? 0;
                          const available = hour.isUnlimited
                            ? null
                            : hour.availableSlots ?? 0;
                          const isOwnSlotInEdit =
                            isEdit &&
                            formData.startTime === hour.startTime &&
                            formData.endTime === hour.endTime;
                          const isFull =
                            !hour.isUnlimited &&
                            (available === 0 || isOwnSlotInEdit);
                          return (
                            <div
                              key={`${hour.startTime}-${hour.endTime}`}
                              className="flex justify-between items-center text-sm border-b border-gray-200 pb-2 last:border-0"
                            >
                              <span className="font-medium text-gray-800">
                                {hour.startTime} – {hour.endTime}
                              </span>
                              <span
                                className={
                                  isOwnSlotInEdit
                                    ? 'text-blue-600 font-medium'
                                    : isFull
                                      ? 'text-red-600 font-medium'
                                      : 'text-green-600 font-medium'
                                }
                              >
                                {hour.isUnlimited
                                  ? 'Không giới hạn'
                                  : isOwnSlotInEdit
                                    ? `Đăng ký này (${total}/${total} ${slotCapacityUnit})`
                                    : isFull
                                      ? 'Đã có đăng ký'
                                      : `Còn ${available}/${total} ${slotCapacityUnit}`}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  ) : availabilityInfo && (
                    <>
                      <div className="text-sm text-gray-500 mb-2">
                        Chọn khung giờ để xem tình trạng chi tiết
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Sức chứa:</span>
                        <span className="font-medium">
                          {availabilityInfo.isUnlimited ? 'Không giới hạn' : `${availabilityInfo.totalCapacity || availabilityInfo.maxCapacity || 0} ${slotCapacityUnit}`}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Đã đặt:</span>
                        <span className="font-medium text-orange-600">{availabilityInfo.bookedSlots} {slotCapacityUnit}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Còn trống:</span>
                        <span className="font-medium text-green-600">
                          {availabilityInfo.isUnlimited ? 'Không giới hạn' : `${availabilityInfo.availableSlots} ${slotCapacityUnit}`}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              )}

              {selectedUtility.group?.utilityType === 'TIME_BASED' && selectedTimeRange && (
                <div className="space-y-2">
                  <div className="text-sm text-blue-600 font-medium mb-2">
                    Thời gian đã chọn: {selectedTimeRange.startHour} - {selectedTimeRange.endHour}
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Thời lượng:</span>
                    <span className="font-medium">{selectedTimeRange.duration} giờ</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Giá trung bình/giờ:</span>
                    <span className="font-medium text-blue-600">
                      {(() => {
                        const selectedHours = timeSliderData.filter(h => h.isSelected);
                        const totalCost = selectedHours.reduce((total, hour) => total + hour.price, 0);
                        const avgPrice = selectedHours.length > 0 ? totalCost / selectedHours.length : 0;
                        return avgPrice.toLocaleString('vi-VN');
                      })()}đ
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Tổng phí dịch vụ:</span>
                    <span className="font-medium text-green-600">
                      {(() => {
                        const selectedHours = timeSliderData.filter(h => h.isSelected);
                        const totalCost = selectedHours.reduce((total, hour) => total + hour.price, 0);
                        return totalCost.toLocaleString('vi-VN');
                      })()} VND
                    </span>
                  </div>
                </div>
              )}

              {selectedUtility.group?.utilityType === 'TIME_BASED' && !selectedTimeRange && timeSliderData.length > 0 && (
                <div className="space-y-2">
                  <div className="text-sm text-gray-500 mb-2">
                    Chọn thời gian trên thanh thời gian để xem tình trạng chi tiết
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Giờ hoạt động:</span>
                    <span className="font-medium">
                      {timeSliderData[0]?.hour} - {addHoursToTime(timeSliderData[timeSliderData.length - 1]?.hour, 1)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Giá/giờ:</span>
                    <span className="font-medium text-blue-600">
                      {(timeSliderData[0]?.price || 0).toLocaleString('vi-VN')} VND
                    </span>
                  </div>
                </div>
              )}

              {selectedUtility.group?.utilityType === 'NO_BOOKING' && (selectedSlotAvailability || availabilityInfo) && (
                <div className="space-y-2">
                  {selectedTimeSlot && selectedSlotAvailability ? (
                    // Show selected time slot availability details
                    <>
                      <div className="text-sm text-blue-600 font-medium mb-2">
                        Khung giờ đã chọn: {formData.startTime} - {formData.endTime}
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Tổng sức chứa:</span>
                        <span className="font-medium">
                          {selectedSlotAvailability.isUnlimited ? 'Không giới hạn' : `${selectedSlotAvailability.totalCapacity} người`}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Đang sử dụng:</span>
                        <span className="font-medium text-orange-600">{selectedSlotAvailability.currentOccupancy || 0} người</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Còn trống:</span>
                        <span className={`font-medium ${selectedSlotAvailability.isAvailable !== false ? 'text-green-600' : 'text-red-600'}`}>
                          {selectedSlotAvailability.isUnlimited ? 'Luôn sẵn sàng' : (() => {
                            const requestedSlots = parseInt(formData.slots) || 1;
                            // Show same value as "Tối đa" field - available slots without subtraction
                            const availableSlots = selectedSlotAvailability.availableSlots || 0;
                            return `${availableSlots} người (sau khi đăng ký ${requestedSlots} người)`;
                          })()}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Trạng thái:</span>
                        <span className={`font-medium ${(() => {
                          if (selectedSlotAvailability.isUnlimited) return true;
                          const requestedSlots = parseInt(formData.slots) || 1;
                          return (selectedSlotAvailability.availableSlots || 0) >= requestedSlots;
                        })() ? 'text-green-600' : 'text-red-600'}`}>
                          {(() => {
                            if (selectedSlotAvailability.isUnlimited) return 'Có thể sử dụng';
                            const requestedSlots = parseInt(formData.slots) || 1;
                            const canBook = (selectedSlotAvailability.availableSlots || 0) >= requestedSlots;
                            return canBook ? 'Có thể sử dụng' : 'Vượt quá sức chứa';
                          })()}
                        </span>
                      </div>
                    </>
                                       ) : availabilityInfo?.serviceHours && (
                     // Show overall availability when no time slot selected
                     <>
                       <div className="text-sm text-gray-500 mb-2">
                         Chọn khung giờ để xem tình trạng chi tiết
                       </div>
                       <div className="flex justify-between items-center">
                         <span className="text-sm text-gray-600">Tổng sức chứa:</span>
                         <span className="font-medium">
                           {(() => {
                             const serviceHours = availabilityInfo.serviceHours;
                             const hasUnlimited = serviceHours.some((hour: any) => hour.isUnlimited);
                             if (hasUnlimited) return 'Không giới hạn';
                             const totalCapacity = serviceHours.reduce((sum: number, hour: any) => sum + (hour.totalCapacity || hour.maxCapacity || 0), 0);
                             return `${totalCapacity} người`;
                           })()}
                         </span>
                       </div>
                       <div className="flex justify-between items-center">
                         <span className="text-sm text-gray-600">Đang sử dụng:</span>
                         <span className="font-medium text-orange-600">
                           {(() => {
                             const serviceHours = availabilityInfo.serviceHours;
                             return serviceHours.reduce((sum: number, hour: any) => sum + (hour.bookedSlots || 0), 0);
                           })()} người
                         </span>
                       </div>
                       <div className="flex justify-between items-center">
                         <span className="text-sm text-gray-600">Còn trống:</span>
                         <span className={`font-medium ${(() => {
                           const serviceHours = availabilityInfo.serviceHours;
                           const hasUnlimited = serviceHours.some((hour: any) => hour.isUnlimited);
                           if (hasUnlimited) return true;
                           const requestedSlots = parseInt(formData.slots) || 1;
                           
                           // Use same calculation as "Tối đa" field for consistency
                           const totalAvailable = serviceHours.reduce((sum: number, hour: any) => sum + (hour.availableSlots || 0), 0);
                           
                           return totalAvailable >= requestedSlots;
                         })() ? 'text-green-600' : 'text-red-600'}`}>
                           {(() => {
                             const serviceHours = availabilityInfo.serviceHours;
                             const hasUnlimited = serviceHours.some((hour: any) => hour.isUnlimited);
                             if (hasUnlimited) return 'Luôn sẵn sàng';
                             const requestedSlots = parseInt(formData.slots) || 1;
                             
                             // Use the same calculation as "Tối đa" field for consistency
                             const totalAvailable = serviceHours.reduce((sum: number, hour: any) => sum + (hour.availableSlots || 0), 0);
                             const simulatedAvailable = Math.max(0, totalAvailable - requestedSlots);
                             
                             return `${simulatedAvailable} người (sau khi đăng ký ${requestedSlots} người)`;
                           })()}
                         </span>
                       </div>
                       <div className="flex justify-between items-center">
                         <span className="text-sm text-gray-600">Trạng thái:</span>
                         <span className={`font-medium ${(() => {
                           const serviceHours = availabilityInfo.serviceHours;
                           const hasUnlimited = serviceHours.some((hour: any) => hour.isUnlimited);
                           if (hasUnlimited) return true;
                           const requestedSlots = parseInt(formData.slots) || 1;
                           
                           // Use same calculation as "Tối đa" field for consistency
                           const totalAvailable = serviceHours.reduce((sum: number, hour: any) => sum + (hour.availableSlots || 0), 0);
                           
                           return totalAvailable >= requestedSlots;
                         })() ? 'text-green-600' : 'text-red-600'}`}>
                           {(() => {
                             const serviceHours = availabilityInfo.serviceHours;
                             const hasUnlimited = serviceHours.some((hour: any) => hour.isUnlimited);
                             if (hasUnlimited) return 'Có thể sử dụng';
                             const requestedSlots = parseInt(formData.slots) || 1;
                             
                             // Use same calculation as "Tối đa" field for consistency
                             const totalAvailable = serviceHours.reduce((sum: number, hour: any) => sum + (hour.availableSlots || 0), 0);
                             
                             const canBook = totalAvailable >= requestedSlots;
                             return canBook ? 'Có thể sử dụng' : 'Vượt quá sức chứa';
                           })()}
                         </span>
                       </div>
                     </>
                   )}
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-1 gap-6">
            {/* Slots/Quantity - Only show for FIXED_SLOTS */}
            {selectedUtility?.group?.utilityType === 'FIXED_SLOTS' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Số lượng <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  name="slots"
                  value={formData.slots}
                  onChange={handleInputChange}
                  min="1"
                  max={
                    selectedSlotAvailability && 
                    !selectedSlotAvailability.isUnlimited 
                      ? getBookableSlotsRemaining(selectedSlotAvailability)
                      : undefined
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="1"
                  required
                />
                <p className="text-sm text-gray-500 mt-1">
                  Số slot muốn đặt
                  {selectedSlotAvailability && 
                   !selectedSlotAvailability.isUnlimited && (
                    <span className="text-orange-600 ml-1">
                      (Tối đa: {getBookableSlotsRemaining(selectedSlotAvailability)} slot)
                    </span>
                  )}
                </p>
              </div>
            )}

            {/* Duration Display - For TIME_BASED */}
            {selectedUtility?.group?.utilityType === 'TIME_BASED' && selectedTimeRange && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Thời lượng đã chọn
                </label>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-lg font-semibold text-blue-800">
                        {selectedTimeRange.duration} giờ
                      </span>
                      <div className="text-sm text-blue-600">
                        {selectedTimeRange.startHour} - {selectedTimeRange.endHour}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-gray-600">Giá/giờ</div>
                      <div className="font-semibold text-gray-800">
                        {(() => {
                          const selectedHour = timeSliderData.find(h => h.isSelected);
                          const price = selectedHour?.price || timeSliderData[0]?.price || 0;
                          return price.toLocaleString('vi-VN');
                        })()}đ
                      </div>
                    </div>
                  </div>
                </div>
                <input
                  type="hidden"
                  name="slots"
                  value={formData.slots}
                />
              </div>
            )}

            {/* Quantity for NO_BOOKING */}
            {selectedUtility?.group?.utilityType === 'NO_BOOKING' && (selectedSlotAvailability || availabilityInfo) && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Số người <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  name="slots"
                  value={formData.slots}
                  onChange={handleInputChange}
                  min="1"
                  max={(() => {
                    if (selectedSlotAvailability) {
                      return selectedSlotAvailability.isUnlimited
                        ? undefined
                        : getBookableSlotsRemaining(selectedSlotAvailability);
                    }
                    return availabilityInfo?.isUnlimited ? undefined : availabilityInfo?.availableSlots;
                  })()}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="1"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  {(() => {
                    if (selectedSlotAvailability) {
                      return selectedSlotAvailability.isUnlimited 
                        ? '💡 Không giới hạn số người' 
                        : `Tối đa ${selectedSlotAvailability.availableSlots} người`;
                    }
                    return availabilityInfo?.isUnlimited 
                      ? '💡 Không giới hạn số người' 
                      : `Tối đa ${availabilityInfo?.availableSlots || 0} người`;
                  })()}
                </p>
              </div>
            )}
          </div>

          {/* Pricing Information for NO_BOOKING */}
          {selectedUtility && selectedUtility.group?.utilityType === 'NO_BOOKING' && selectedTimeSlot && availableTimeSlots.length > 0 && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Thông tin thanh toán</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Chi phí sử dụng</label>
                  <div className="text-lg font-semibold text-gray-900">
                    {(() => {
                      const selectedSlot = availableTimeSlots.find(s => 
                        s.startTime === formData.startTime && s.endTime === formData.endTime
                      );
                      const slotPrice = selectedSlot?.price || 0;
                      const numberOfPeople = parseInt(formData.slots) || 1;
                      const totalCost = slotPrice * numberOfPeople;
                      return totalCost.toLocaleString('vi-VN');
                    })()} VND
                  </div>
                  <p className="text-sm text-gray-500">
                    {formData.slots} người × {(() => {
                      const selectedSlot = availableTimeSlots.find(s => 
                        s.startTime === formData.startTime && s.endTime === formData.endTime
                      );
                      return (selectedSlot?.price || 0).toLocaleString('vi-VN');
                    })()}đ/người
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Khung giờ đã chọn</label>
                  <div className="text-lg font-semibold text-blue-600">
                    {formData.startTime} - {formData.endTime}
                  </div>
                  <p className="text-sm text-gray-500">
                    {(() => {
                      const selectedSlot = availableTimeSlots.find(s => 
                        s.startTime === formData.startTime && s.endTime === formData.endTime
                      );
                      return selectedSlot?.availableSlots === 999 
                        ? 'Không giới hạn' 
                        : `${selectedSlot?.availableSlots || 0} người còn lại`;
                    })()}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Pricing Information */}
          {selectedUtility && selectedUtility.group?.utilityType !== 'NO_BOOKING' && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Thông tin thanh toán</h3>
              <div
                className={`grid grid-cols-1 gap-4 ${
                  calculatedCost > 0 && depositAmount > 0
                    ? 'md:grid-cols-3'
                    : calculatedCost > 0 || depositAmount > 0
                      ? 'md:grid-cols-2'
                      : 'md:grid-cols-1'
                }`}
              >
                {calculatedCost > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Chi phí dịch vụ</label>
                  <div className="text-lg font-semibold text-gray-900">
                    {selectedUtility.group?.utilityType === 'TIME_BASED' && selectedTimeRange ? (
                      <>
                        {(() => {
                          const selectedHours = timeSliderData.filter(h => h.isSelected);
                          const totalCost = selectedHours.reduce((total, hour) => total + hour.price, 0);
                          return totalCost.toLocaleString('vi-VN');
                        })()} VND
                      </>
                    ) : (
                      <>
                        {calculatedCost.toLocaleString('vi-VN')} VND
                      </>
                    )}
                  </div>
                  <p className="text-sm text-gray-500">
                    {selectedUtility.group?.utilityType === 'TIME_BASED' && selectedTimeRange ? (
                      <>
                        {selectedTimeRange.duration} giờ × {(() => {
                          const selectedHour = timeSliderData.find(h => h.isSelected);
                          const price = selectedHour?.price || timeSliderData[0]?.price || 0;
                          return price.toLocaleString('vi-VN');
                        })()}đ/giờ
                      </>
                    ) : (
                      <>
                        {formData.slots} ×{' '}
                        {selectedUtility?.group?.utilityType === 'FIXED_SLOTS'
                          ? 'slot'
                          : selectedUtility?.group?.utilityType === 'NOTIFICATION_ONLY'
                            ? 'đăng ký'
                            : 'giờ'}
                      </>
                    )}
                  </p>
                </div>
                )}
                {depositAmount > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Tiền đặt cọc</label>
                  <div className="text-lg font-semibold text-orange-600">
                    {depositAmount.toLocaleString('vi-VN')} VND
                  </div>
                  <p className="text-sm text-gray-500">Phí đặt cọc (thu riêng khi xác nhận)</p>
                </div>
                )}
                {(calculatedCost > 0 || depositAmount > 0) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Tổng thanh toán</label>
                  <div className="text-xl font-bold text-green-600">
                    {(calculatedCost + depositAmount).toLocaleString('vi-VN')} VND
                  </div>
                  <p className="text-sm text-gray-500">
                    {calculatedCost > 0 && depositAmount > 0
                      ? 'Dịch vụ + Đặt cọc'
                      : depositAmount > 0
                        ? 'Chỉ tiền đặt cọc'
                        : 'Chi phí dịch vụ'}
                  </p>
                </div>
                )}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Total Cost (Hidden, calculated automatically) */}
            <div className="hidden">
              <input
                type="number"
                name="totalCost"
                value={formData.totalCost}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Trạng thái
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {Object.values(BookingStatus).map((status) => (
                  <option key={status} value={status}>
                    {getStatusLabel(status)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Ghi chú
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Ghi chú thêm về đăng ký này..."
            />
          </div>

          {isEdit && (
            <RegistrationActivityLogsPanel logs={activityLogs} loading={logsLoading} />
          )}
          </fieldset>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={() => navigate('/building-utility-bookings')}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              {isReadOnly ? 'Quay lại' : 'Hủy'}
            </button>
            {!isReadOnly && (
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg hover:from-cyan-600 hover:to-blue-600 transition-all duration-200 shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Đang xử lý...' : (isEdit ? 'Cập nhật' : 'Tạo mới')}
            </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}; 